import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type FavoriteField = "word" | "regex" | "word2" | "regex2";
export type FavoriteSettingsEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/favorite-draft-changed"
      | "settings/favorite-add-requested"
      | "settings/favorite-remove-requested";
  }
>;

export type FavoriteSettingsState = Readonly<Record<FavoriteField, string>>;

export type FavoriteSettingsEffect =
  | {
      readonly type: "add" | "remove";
      readonly field: FavoriteField;
      readonly value: string;
    }
  | { readonly type: "alert"; readonly message: string };

const EMPTY_STATE: FavoriteSettingsState = {
  word: "",
  regex: "",
  word2: "",
  regex2: "",
};

function emptyMessage(field: FavoriteField): string {
  if (field === "word2") return "超お気に入りワードを入力してください";
  if (field === "word") return "お気に入りワードを入力してください";
  return "正規表現パターンを入力してください";
}

export function createFavoriteSettingsMediator(
  runEffect: (effect: FavoriteSettingsEffect) => void,
): Mediator<
  FavoriteSettingsState,
  FavoriteSettingsEvent,
  FavoriteSettingsEffect
> {
  return createMediator<
    FavoriteSettingsState,
    FavoriteSettingsEvent,
    FavoriteSettingsEffect
  >(
    EMPTY_STATE,
    (state, event) => {
      if (event.type === "settings/favorite-draft-changed") {
        return {
          status: "handled",
          state: { ...state, [event.field]: event.value },
        };
      }
      if (event.type === "settings/favorite-remove-requested") {
        return {
          status: "handled",
          state,
          effects: [{ type: "remove", field: event.field, value: event.value }],
        };
      }

      const value = state[event.field].trim();
      if (!value) {
        return {
          status: "handled",
          state,
          effects: [{ type: "alert", message: emptyMessage(event.field) }],
        };
      }
      if (event.field === "regex" || event.field === "regex2") {
        try {
          new RegExp(value);
        } catch {
          return {
            status: "handled",
            state,
            effects: [{ type: "alert", message: "無効な正規表現パターンです" }],
          };
        }
      }
      return {
        status: "handled",
        state: { ...state, [event.field]: "" },
        effects: [{ type: "add", field: event.field, value }],
      };
    },
    { runEffect },
  );
}
